import React from 'react';
import PropTypes from 'prop-types';
import { 
  Card, 
  CardContent, 
  Typography, 
  Box, 
  FormControlLabel, 
  RadioGroup, 
  Radio, 
  Paper, 
  Button,
  Stack,
  Chip
} from '@mui/material';
import { CloudUpload } from '@mui/icons-material';
import { CustomMultiSelect } from '../../../../components/CustomMultiSelect';
import { ValidationError } from '../ValidationError';
import { PROMOTION_AVAILABILITY_OPTIONS } from '../../constants';

export const PromotionAvailability = ({ 
  formData, 
  onInputChange,
  citiesOptions,
  uploadedFiles,
  onFileUpload,
  onRemoveFile,
  formErrors = {}
}) => {
  return (
    <Card sx={{ mb: 3, boxShadow: 2, overflow: 'visible' }}>
      <CardContent>
        <Typography variant="h5" sx={{ mb: 3, fontWeight: 600, color: 'black' }}>
          Настройки доступности акции
        </Typography>
        
        <Box sx={{ mb: 3 }}>
          <CustomMultiSelect
            placeholder="Выберите города, где будет доступна акция"
            options={[
              { label: 'Все', value: 'all' },
              ...citiesOptions
            ]}
            value={formData.cities}
            onChange={(value) => onInputChange('cities', value)}
            required
            width={498}
            showAll={true}
            error={!!formErrors.cities}
          />
          <ValidationError error={formErrors.cities} />
        </Box>

        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 500 }}>
            Кому показывать акцию?
          </Typography>
          <RadioGroup
            value={formData.availability_type}
            onChange={(e) => onInputChange('availability_type', e.target.value)}
          >
            {PROMOTION_AVAILABILITY_OPTIONS.map(option => (
              <FormControlLabel
                key={option.value}
                value={option.value}
                control={<Radio color="primary" />}
                label={option.label}
                sx={{ mb: 1 }}
              />
            ))}
          </RadioGroup>
        </Box>

        {formData.availability_type === 'customer_id' && (
          <Paper 
            elevation={1}
            sx={{ 
              p: 3, 
              border: formErrors.file_ids ? '2px dashed #f44336' : '2px dashed #e0e0e0', 
              borderRadius: 2,
              backgroundColor: formErrors.file_ids ? '#ffebee' : 'grey.50',
              transition: 'all 0.3s ease',
              '&:hover': {
                borderColor: formErrors.file_ids ? '#f44336' : 'primary.main',
                backgroundColor: formErrors.file_ids ? '#ffebee' : 'primary.50'
              }
            }}
          >
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 500 }}>
              Загрузить файл со списком
            </Typography>
            <input
              type="file"
              accept=".xlsx,.xls,.csv"
              onChange={onFileUpload}
              style={{ display: 'none' }}
              id="file-upload"
            />
            <label htmlFor="file-upload">
              <Button
                component="span"
                startIcon={<CloudUpload />}
                variant="contained"
                sx={{ borderRadius: 2 }}
              >
                Выбрать файл
              </Button>
            </label>
            {uploadedFiles.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  {uploadedFiles.map((file, index) => (
                    <Chip
                      key={file.id || index}
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <span>{file.name}</span>
                          {file.status === 'uploading' && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box 
                                sx={{ 
                                  width: 16, 
                                  height: 16, 
                                  border: '2px solid #1976d2',
                                  borderTop: '2px solid transparent',
                                  borderRadius: '50%',
                                  animation: 'spin 1s linear infinite',
                                  '@keyframes spin': {
                                    '0%': { transform: 'rotate(0deg)' },
                                    '100%': { transform: 'rotate(360deg)' }
                                  }
                                }}
                              />
                              <Typography variant="caption" sx={{ fontSize: '10px' }}>
                                {file.progress}%
                              </Typography>
                            </Box>
                          )}
                          {file.status === 'success' && (
                            <Box sx={{ color: 'success.main', fontSize: '14px' }}>✓</Box>
                          )}
                          {file.status === 'error' && (
                            <Box sx={{ color: 'error.main', fontSize: '14px' }}>✗</Box>
                          )}
                        </Box>
                      }
                      color={
                        file.status === 'success' ? 'success' : 
                        file.status === 'error' ? 'error' : 
                        'default'
                      }
                      variant={file.status === 'success' ? 'filled' : 'outlined'}
                      size="small"
                      onDelete={file.status === 'success' ? () => onRemoveFile(index) : undefined}
                    />
                  ))}
                </Stack>
              </Box>
            )}
            <ValidationError error={formErrors.file_ids} sx={{ mt: 1 }} />
          </Paper>
        )}
      </CardContent>
    </Card>
  );
};

PromotionAvailability.propTypes = {
  formData: PropTypes.object.isRequired,
  onInputChange: PropTypes.func.isRequired,
  citiesOptions: PropTypes.array.isRequired,
  uploadedFiles: PropTypes.array.isRequired,
  onFileUpload: PropTypes.func.isRequired,
  onRemoveFile: PropTypes.func.isRequired,
  formErrors: PropTypes.object
};
